/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { EventEmitter } from '@angular/core';
import { Icon } from '../../common/iconSettings';
import {
  WorkflowService,
  Service,
  Workflow,
} from '../services/workflow.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AutomatedWorkflowService {
  baseUrl = 'api/automatedWorkflow/';
  workflows: AutomatedWorkflow[];
  deleteWorkflows: EventEmitter<boolean>;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private workflowService: WorkflowService,
  ) {
    this.deleteWorkflows = new EventEmitter();
  }

  getWorkflows(getJobs = true): Observable<AutomatedWorkflow[]> {
    this.workflowService.loadOptionList();
    this.workflowService.loadSqdServices();

    // Only make this call if we are logged in
    if (this.userService.user) {
      return this.http.get<AutomatedWorkflow[]>(
        `${this.baseUrl}All/${this.userService.user.id}/${this.userService.user.accessListId}`,
        { params: { getJobs: getJobs } },
      );
    } else {
      // return an empty array if a user is not logged in
      return new Observable<AutomatedWorkflow[]>((observer) => {
        observer.next([]);
        observer.complete();
      });
    }
  }

  getWorkflow(workflow: AutomatedWorkflow) {
    return this.http.get<AutomatedWorkflow>(
      `${this.baseUrl}${workflow.owner}/${workflow.id}`,
    );
  }

  createWorkflow(workflow: AutomatedWorkflow) {
    // new workflows require null id
    workflow.id = null;

    // Set creator to the current User
    workflow.creator = this.userService.user?.id ?? '';

    // owner is accessListId for encrypting purposes
    workflow.owner = this.userService.user?.accessListId ?? '';
    // get timezoneOffset of user so jobs date/time can be correct
    workflow.timezoneOffset = (new Date().getTimezoneOffset() * -1) / 60;

    // save email for error notifications
    workflow.errorEmailAddress = this.userService.user?.email ?? '';

    const services = workflow.services;
    // set service creator with user id
    if (services && (services[0] as Service).id !== undefined) {
      (services as Service[]).forEach(
        (s) => (s.creator = this.userService.user?.id ?? ''),
      );
    }
    const newWorkflow = JSON.parse(JSON.stringify(workflow));
    //convert full services back to PageBreak + service id
    if (
      newWorkflow.services &&
      (newWorkflow.services[0] as Service).id !== undefined
    ) {
      newWorkflow.services = (newWorkflow.services as Service[]).map((s) =>
        s.pageBreak ? '1' + s.id : '0' + s.id,
      );
    }
    return this.http.post<string>(this.baseUrl, {
      workflow: newWorkflow,
      services: services,
    });
  }

  runWorkflowNow(accessListId: string, workflow: AutomatedWorkflow) {
    return this.http.post<string>(this.baseUrl + 'ExecuteWorkflowNow', {
      accessListId: accessListId,
      workflowId: workflow.id,
      lastRun: workflow.lastRun,
    });
  }

  deleteWorkflow(workflow: AutomatedWorkflow) {
    const owner = workflow.owner;
    const id = workflow.id;
    let serviceNames: string;

    // workflow.services contain service ids only, so get full services to get service names
    if (typeof workflow?.services?.[0] === 'string') {
      const services: Service[] = [];
      workflow.services.forEach((x) => {
        if (typeof x === 'string') {
          services.push(this.workflowService.getSqdService(x));
        }
      });
      serviceNames = services.map((x) => x.name).join();
    }
    // workflow.services are full service objects
    else {
      serviceNames =
        (workflow?.services as Service[])?.map((x) => x.name).join() ?? '';
    }

    return this.http.delete<string>(
      `${this.baseUrl}${owner}/${id}/${serviceNames}`,
    );
  }

  getJobHistory(workflowId: string) {
    return this.http.get<AutomatedJobHistory[]>(
      `${this.baseUrl}history/${workflowId}`,
    );
  }

  deleteJobHistory(workflowId: string, jobIds: string[]) {
    return this.http.delete<AutomatedJobHistory[]>(
      `${this.baseUrl}history/${workflowId}`,
      { body: jobIds },
    );
  }

  toggleDeleteWorkflow(toggle: boolean) {
    this.deleteWorkflows.emit(toggle);
  }

  isWorkflowWithSameNameExist(id: string, name: string) {
    const owner = this.userService.user?.accessListId;
    const index = this.workflows.findIndex(
      (w) => w.owner == owner && w.name === name && w.id != id,
    );
    return index != -1;
  }

  saveWorkflow(workflow: AutomatedWorkflow) {
    if (workflow.isOrphan) {
      // If this is an orphaned workflow set the creator to the current user/Access List Manager/Owner
      workflow.isOrphan = false;
      workflow.creator = this.userService.user?.id ?? '';
    }

    // update timezoneOffset in case it changed so jobs date/time can be correct
    workflow.timezoneOffset = (new Date().getTimezoneOffset() * -1) / 60;
    workflow.owner = this.userService.user?.accessListId ?? '';
    workflow.errorEmailAddress = this.userService.user?.email ?? '';

    // make a copy to save
    const saveWorkflow = JSON.parse(JSON.stringify(workflow));

    // workflow.services contain service ids only, so get service objects
    let services: Service[] = [];
    if (typeof saveWorkflow.services[0] === 'string') {
      saveWorkflow.services.forEach((x: string) =>
        services.push(
          this.workflowService.getSqdService(x, workflow.type === 1),
        ),
      );
    } else {
      services = saveWorkflow.services;
      // set service creator
      services.forEach((s) => (s.creator = saveWorkflow.creator));
      //convert full services back to PageBreak + service id
      saveWorkflow.services = services.map((s) =>
        s.pageBreak ? '1' + s.id : '0' + s.id,
      );
    }

    return this.http.put<string>(this.baseUrl, {
      workflow: saveWorkflow,
      services: services,
    });
  }
}

/**
 * WorkflowView
 */
export interface AutomatedWorkflow extends Omit<
  Workflow,
  | 'isShared'
  | 'isEncryptAndTrackDisabled'
  | 'isDisabled'
  | 'interimDownloaded'
  | 'canBeDeleted'
  | 'isHidden'
  | 'parentName'
  | 'parentId'
  | 'icon'
> {
  parentId?: string | null;
  icon: Icon | undefined;
  succeededJobs: number;
  failedJobs: number;
  enabled: boolean;
  schedule: {
    occurrence: string;
    date: string;
    occurrenceUpdated: boolean;
  };
  emailOptions: EmailOptions;
  timezoneOffset: number;
  errorEmailAddress: string;
  lastRun: Date;
  nextRun: Date;
  jobStatus?: {
    completed: number;
    failed: number;
  };
  jobs?: number;
  version: string;
}

export interface EmailOptions {
  emailAddress: string;
  from: EmailField;
  subject: EmailField;
  processAttachments: boolean;
  processMessage: boolean;
}

export interface EmailField {
  queryStr: string;
  matchMode: EmailFieldMatchMode;
}
export enum EmailFieldMatchMode {
  Matches,
  Contains,
}

export interface AutomatedJobHistory {
  workflowId: string;
  id: string;
  pageCount: number;
  status: JobStatus[];
  occurrence: Occurrence;
  jobOccurrence: Occurrence;
  initiatedAt: string;
  submitted?: string;
  occurrenceString?: string;
  pageCountString?: string;
  hasHeader?: boolean;
  checked?: boolean;
}

export interface JobStatus {
  state: number;
  status: string;
  statusRaw: string;
  serviceId: string;
  serviceName: string;
  pagesCompleted: number;
}
export interface Occurrence {
  occurrence: string;
  date: string;
}
